import { Banknote, Key, UserCircle2 } from 'lucide-vue-next'
import ColumnLink from 'pejer/src/components/Table/core/ColumnLink.vue'
import { h } from 'vue'
import { Groups, JSSchema } from '.'
import { CustomerTypeList, MoneyFormatter } from './Global'
import { ColorPalette, textWithIconCell } from './helpers'

export namespace Customer {
  export type CustomerType = 'Unknown' | 'Individual' | 'Business'
  // extends ToListItem<typeof CustomerSchema> {}
  export interface ListItem {
    customerId: number
    name: string
    email: string
    customerType: CustomerType
    mobilePrefix: string
    customerExternalID: string
    mobile: string
    numberOfPayments: number
    amountSum: number
    lastPayment: string
  }

  export interface Full extends ListItem {}
}

export const CustomerGroups = {
  'Personal information': {
    icon: UserCircle2,
    color: ColorPalette.blue,
    isMainGroup: true,
    mainColumn: 'name',
  },
  'Number of Payments': {
    icon: Banknote,
    color: ColorPalette.green,
    mainColumn: 'numberOfPayments',
  },
  ID: {
    icon: Key,
    color: ColorPalette.pink,
    mainColumn: 'customerId',
  },
} satisfies Groups<Customer.ListItem>

export const CustomerListSchema: JSSchema<Customer.ListItem, typeof CustomerGroups> = {
  name: 'Customer',
  title: 'Customers',
  groups: CustomerGroups,
  fields: {
    customerId: {
      type: 'string',
      humanReadable: 'ID',
      meta: {
        group: 'ID',
      },
      isDefaultSortColumn: true,
      formattedCell: (value: string) => {
        return h(
          ColumnLink,
          {
            content: value,
            id: value,
            pathName: 'customer',
          },
          () => textWithIconCell(Key, value)
        )
      },
    },
    name: {
      type: 'string',
      humanReadable: 'Name',
      smartSearchType: 'Lax',
      meta: {
        group: 'Personal information',
      },
      specificCell:
        (self) =>
        ({ row }) => {
          return h(
            ColumnLink,
            {
              id: row.original.customerId,
              pathName: 'customer',
              content: row.original.name,
            },
            () => row.original.name
          )
        },
      sensitive: true,
    },
    email: {
      type: 'string',
      humanReadable: 'Email',
      smartSearchType: 'Lax',
      metaType: 'email',
      meta: {
        group: 'Personal information',
      },
      sensitive: true,
    },
    customerType: {
      type: 'enum',
      humanReadable: 'Customer type',
      options: CustomerTypeList,
      meta: {
        group: 'Personal information',
      },
    },
    mobilePrefix: {
      type: 'string',
      humanReadable: 'Mobile Phone Prefix',
      meta: { hideFromTable: true, group: 'Personal information' },
    },
    mobile: {
      type: 'string',
      humanReadable: 'Phone Number',
      metaType: 'phone',
      prefixColumn: 'mobilePrefix',
      meta: {
        group: 'Personal information',
      },
      sensitive: true,
    },
    customerExternalID: {
      type: 'string',
      humanReadable: 'External Identifier',
      meta: {
        group: 'ID',
      },
    },
    numberOfPayments: {
      type: 'number',
      humanReadable: 'Number of Payments Made',
      meta: {
        group: 'Number of Payments',
      },
    },
    amountSum: {
      type: 'number',
      humanReadable: 'Sum of Payments Made',
      formatter: MoneyFormatter,
      metaType: 'money',
      meta: {
        group: 'Number of Payments',
      },
    },
    lastPayment: {
      type: 'date',
      humanReadable: 'Last Payment',
      meta: {
        group: 'Number of Payments',
      },
    },
  },
}

export const CustomerFullSchema: JSSchema<Customer.Full, typeof CustomerGroups> = {
  ...CustomerListSchema,
  title: 'Customer',
}
